import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import useSeo from "@/hooks/useSeo";
import { getMoviesByIds, getNewsById } from "@/utils/data";

export default function NewsDetail() {
  const { id = "" } = useParams();
  const item = getNewsById(id);

  useSeo({
    title: item ? `${item.title} - 百合影视资讯` : "资讯未找到 - 百合影视",
    description: item
      ? item.summary
      : "该条百合影视资讯不存在或已删除。",
    keywords: item
      ? `百合影视资讯,${item.title},百合新闻,影视动态`
      : "百合影视资讯",
  });

  if (!item) {
    return (
      <div className="pt-24 md:pt-32">
        <div className="mx-auto flex max-w-[1280px] flex-col items-center justify-center px-4 py-24 text-center md:px-6">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-3xl text-foreground-400">
            <i className="ri-article-line" />
          </span>
          <h1 className="mt-5 font-heading text-xl font-semibold text-foreground-950">
            未找到该条资讯
          </h1>
          <p className="mt-2 max-w-md text-sm text-foreground-600">
            你访问的资讯可能已被移除，或者链接有误。
          </p>
          <Link
            to="/news"
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            返回资讯列表
          </Link>
        </div>
      </div>
    );
  }

  const related = getMoviesByIds(item.relatedMovieIds);

  return (
    <div className="pt-24 md:pt-32">
      <article className="mx-auto max-w-[820px] px-4 py-10 md:px-6 md:py-14">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "新闻资讯", to: "/news" },
            { label: "正文" },
          ]}
        />

        <h1 className="mt-5 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
          {item.title}
        </h1>

        <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-foreground-500">
          <span className="flex items-center gap-1.5">
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-calendar-line" />
            </span>
            发布时间：{item.date}
          </span>
          <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-secondary-900">
            百合影视资讯
          </span>
        </div>

        <div className="mt-8 overflow-hidden rounded-lg bg-secondary-100">
          <div className="aspect-[16/9] w-full">
            <img
              src={item.cover}
              alt={`${item.title} 配图`}
              title={`${item.title} 百合影视资讯`}
              className="h-full w-full object-cover object-top"
            />
          </div>
        </div>

        <p className="mt-8 border-l-4 border-primary-300 bg-primary-50 px-5 py-4 text-sm leading-relaxed text-foreground-700 md:text-base">
          {item.summary}
        </p>

        <div className="mt-8 space-y-5">
          {item.content.map((paragraph) => (
            <p
              key={paragraph.slice(0, 14)}
              className="text-sm leading-loose text-foreground-700 md:text-base"
            >
              {paragraph}
            </p>
          ))}
        </div>

        <div className="mt-10">
          <Link
            to="/news"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300/70 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-arrow-left-line" />
            </span>
            返回资讯列表
          </Link>
        </div>
      </article>

      {related.length > 0 && (
        <section className="bg-secondary-50 py-12 md:py-16">
          <div className="mx-auto max-w-[1280px] px-4 md:px-6">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              相关影视推荐
            </h2>
            <p className="mt-2 text-sm text-foreground-600">
              与这条资讯相关的百合影视作品
            </p>
            <div className="mt-8 grid grid-cols-2 gap-x-5 gap-y-8 sm:grid-cols-3 lg:grid-cols-4">
              {related.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}